/**
 * Anti-duplication sonore centralisé (JS / Web).
 *
 * Séparé du cache UI (`PusherService` / `recentEventIds`) : marquer un son joué
 * côté natif (`eventProcessed`) ne doit pas bloquer l'affichage UI du même event.
 *
 * Aligné avec SoundEffectsHelper (Android) et PusherBackgroundManager (iOS) :
 * allowlist `SOUND_EVENT_TYPES`, cooldown 3s, TTL dédup 15 min.
 */

export const NOTIFICATION_SOUND_EVENT_TYPES = new Set([
  "POP_PRE_ASSIGN_COMMAND",
  "NEW_PRE_ASSIGN_DEMANDE",
  "ASSIGNED_DEMANDE_CLIENT",
  "ADMIN_MESSAGE",
  "CREATION_DEVIS_CLIENT",
  "CANCELED_PRESTATAIRE_DEMANDE",
  "ONROAD_DEMANDE_CANCELED",
  "INFORM_PRESTATAIRE_DEMANDE",
  "SIGNAL_PROBLEM_PROVIDER",
  "ADMIN_CANCELED_DEMANDE",
  "DISCUSSION",
  "NEW_DEMANDE",
  "DESTINATION_ATTENT_CLIENT",
  "CHANGE_STATUT_DEVIS",
  "APPLY_PROVIDER_DEVIS",
  "CONFIRM_ARRIVAL_PRESTATAIRE",
]);

/**
 * Events qui déclenchent l'alerte sonore urgente (sonnerie loopée) côté prestataire.
 * Nouvelle mission → sonnerie forte répétée jusqu'à acceptation ou expiration.
 * Doit rester aligné avec :
 * - Android SoundEffectsHelper.java::URGENT_SOUND_EVENT_TYPES
 * - iOS PusherBackgroundManager.swift::urgentSoundEventTypes
 */
export const URGENT_SOUND_EVENT_TYPES = new Set([
  "NEW_DEMANDE",
  "NEW_PRE_ASSIGN_DEMANDE",
  "INFORM_PRESTATAIRE_DEMANDE",
]);

/**
 * Events qui ARRÊTENT l'alerte urgente loopée (expiration ou annulation demande).
 */
export const STOP_URGENT_SOUND_EVENT_TYPES = new Set([
  "POP_PRE_ASSIGN_COMMAND",
  "CANCELED_PRESTATAIRE_DEMANDE",
  "ONROAD_DEMANDE_CANCELED",
  "ADMIN_CANCELED_DEMANDE",
]);

/** TTL dédup par event (reconnect Wi-Fi longue coupure). */
export const NOTIFICATION_SOUND_DEDUP_TTL_MS = 15 * 60_000;

/** Anti-rafale entre events distincts (aligné natif 3s). */
export const NOTIFICATION_SOUND_COOLDOWN_MS = 3_000;

export class NotificationSoundDedup {
  private static readonly soundKeys = new Map<string, number>();
  private static lastGlobalSoundAt = 0;

  static normalizeEventId(value: unknown): string | null {
    if (value == null) {
      return null;
    }
    const normalized = String(value).trim();
    return normalized.length > 0 ? normalized : null;
  }

  static buildDedupKey(data: Record<string, unknown> | null | undefined): string | null {
    if (!data) {
      return null;
    }
    const eventId = NotificationSoundDedup.normalizeEventId(data["event_id"]);
    if (eventId) {
      return `id:${eventId}`;
    }
    const type =
      (data["type_event"] as string) ??
      (data["event_type"] as string) ??
      (data["type_notification"] as string) ??
      (data["event"] as string);
    if (!type || !NOTIFICATION_SOUND_EVENT_TYPES.has(type)) {
      return null;
    }
    const demandRef =
      data["demande"] ??
      data["demande_id"] ??
      data["demand_id"] ??
      data["id_demande"] ??
      data["id"];
    if (demandRef == null) {
      return null;
    }
    return `fb:${type}:${demandRef}`;
  }

  static isGlobalCooldownActive(): boolean {
    return (
      Date.now() - NotificationSoundDedup.lastGlobalSoundAt <
      NOTIFICATION_SOUND_COOLDOWN_MS
    );
  }

  private static wasSoundKeyPlayedRecently(key: string): boolean {
    const now = Date.now();
    const seenAt = NotificationSoundDedup.soundKeys.get(key);
    return seenAt != null && now - seenAt < NOTIFICATION_SOUND_DEDUP_TTL_MS;
  }

  /**
   * True → ne pas jouer le son (déjà joué pour cette clé ou cooldown global).
   */
  static shouldSkipSound(data?: Record<string, unknown> | null): boolean {
    if (NotificationSoundDedup.isGlobalCooldownActive()) {
      return true;
    }
    const key = NotificationSoundDedup.buildDedupKey(data);
    if (key && NotificationSoundDedup.wasSoundKeyPlayedRecently(key)) {
      return true;
    }
    return false;
  }

  /** Enregistre qu'un son a été joué (JS ou sync natif). */
  static markSoundPlayed(data?: Record<string, unknown> | null): void {
    NotificationSoundDedup.lastGlobalSoundAt = Date.now();
    const key = NotificationSoundDedup.buildDedupKey(data);
    if (!key) {
      return;
    }
    NotificationSoundDedup.purgeExpiredSoundKeys();
    NotificationSoundDedup.soundKeys.set(key, Date.now());
  }

  static markSoundPlayedFromNative(data: Record<string, unknown>): void {
    NotificationSoundDedup.markSoundPlayed(data);
  }

  static clear(): void {
    NotificationSoundDedup.soundKeys.clear();
    NotificationSoundDedup.lastGlobalSoundAt = 0;
  }

  private static purgeExpiredSoundKeys(): void {
    if (NotificationSoundDedup.soundKeys.size <= 200) {
      return;
    }
    const now = Date.now();
    const ttl = NOTIFICATION_SOUND_DEDUP_TTL_MS;
    for (const [k, ts] of NotificationSoundDedup.soundKeys) {
      if (now - ts > ttl) {
        NotificationSoundDedup.soundKeys.delete(k);
      }
    }
  }
}
